import * as React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTheme } from '@emotion/react';
import {
  CloseAlt,
  Draft,
  ErrorAlt,
  IconButton,
  Inline,
  Stack,
  Text,
  ThemeProvider,
  useToast,
  theme as resiTheme,
} from '@resi-media/resi-ui';
import type { AxiosError } from 'axios';
import { useAtomValue } from 'jotai';
import { ServerErrorDetailsModal } from '@studio/components/ServerErrorDetailsModal';
import { usePrefix } from '@studio/hooks/use-prefix';
import { isResiAdminAtom } from '@studio/storeV2/admin-tools/admin';
import { extractErrorDetails } from '../extractErrorDetails';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LAUNCHDARKLY_TOGGLES } from '@studio/constants/launchdarkly-toggle-constants';

type _ToastContentProps = {
  message: string;
  name: string;
  onDismiss: () => void;
  onInfo: () => void;
  prefixNS: (key: string) => string;
  statusCode: number;
};

const PRIVATE_ApiErrorToastContent = ({
  message,
  name,
  onDismiss,
  onInfo,
  prefixNS,
  statusCode,
}: _ToastContentProps) => {
  const theme = useTheme();

  return (
    <Inline alignItems="center" gap="m" justifyContent="space-between" width="scale">
      <Inline alignItems="center" gap="s" width="scale" overflow="hidden">
        <ErrorAlt
          data-testid="api-error-toast-icon"
          style={{ color: theme.palette.negative.main, fontSize: theme.typography.pxToRem(20) }}
        />
        <Stack gap="0" overflow="hidden">
          <Text dataTestId="api-error-toast-status" variant="inherit" weightVariant="semiBold">
            {statusCode} {name}
          </Text>
          <Text dataTestId="api-error-toast-message" variant="inherit" truncate title={message}>
            {message}
          </Text>
        </Stack>
      </Inline>
      <Inline alignItems="center" gap="xs">
        <IconButton
          colorVariant="neutral"
          dataTestId="api-error-toast-info-button"
          label={prefixNS('title')}
          onClick={onInfo}
          sizeVariant="s"
        >
          <InfoCircleOutlined />
        </IconButton>
        <IconButton
          colorVariant="neutral"
          dataTestId="api-error-toast-close-button"
          label="Dismiss"
          onClick={onDismiss}
          sizeVariant="s"
        >
          <CloseAlt />
        </IconButton>
      </Inline>
    </Inline>
  );
};

const PRIVATE_ApiErrorToastWrapper = (props: _ToastContentProps) => {
  const parentTheme = useTheme();
  const invertedMode = parentTheme.palette.activeMode === 'dark' ? 'light' : 'dark';

  return (
    <ThemeProvider mode={invertedMode} theme={resiTheme}>
      <PRIVATE_ApiErrorToastContent {...props} />
    </ThemeProvider>
  );
};

const useApiErrorToast = () => {
  const { prefixNS } = usePrefix('components:', 'serverErrorDetails');
  const theme = useTheme();
  const { remove, trigger } = useToast();
  const { setModal, setModalState } = Draft.ModalContext.useModal<ServerErrorDetailsModal.State>();
  const isResiAdmin = useAtomValue(isResiAdminAtom);
  const { [LAUNCHDARKLY_TOGGLES.ERROR_TOASTS]: errorToasts } = useFlags();

  const triggerApiErrorToast = React.useCallback(
    (err: AxiosError) => {
      if (!isResiAdmin || !errorToasts) return;

      const { message, name, requestId, statusCode } = extractErrorDetails(err);
      const toastId = requestId || `error-${Date.now()}`;

      trigger({
        id: toastId,
        timeout: 0,
        content: (
          <PRIVATE_ApiErrorToastWrapper
            message={message}
            name={name}
            onDismiss={() => remove(toastId)}
            onInfo={() => {
              setModalState({ statusCode, name, requestId, message });
              setModal(() => <ServerErrorDetailsModal />, {
                dataTestId: 'server-error-details-modal',
                minHVariant: 's',
                style: { overlay: { zIndex: theme.zIndex.toast + 1 } },
              });
            }}
            prefixNS={prefixNS}
            statusCode={statusCode}
          />
        ),
        shouldCloseOnTimeout: false,
      });
    },
    [errorToasts, isResiAdmin, prefixNS, remove, setModal, setModalState, theme, trigger]
  );

  return { triggerApiErrorToast };
};

export default useApiErrorToast;
